import React, { useState, useCallback, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate } from '../utils/utils';
import DDRPDFExporter from '../components/DDRPDFExporter';

// Hook for DDR PDF Export functionality
const useDDRPDFExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportRef = useRef(null);

  // Export to PDF function
  const exportToPDF = useCallback(async (well, currentReport) => {
    if (!exportRef.current) {
      throw new Error('PDF content not rendered');
    }

    try {
      console.log('🚀 Starting DDR PDF Export from Hook');
      
      // Configure html2canvas options for better quality
      const canvas = await html2canvas(exportRef.current, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: exportRef.current.scrollWidth,
        height: exportRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth - 20; // 10mm margin on each side
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let position = 10; // Top margin

      // Add content to PDF, splitting across pages if necessary
      if (imgHeight <= pdfHeight - 20) {
        // Single page
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      } else {
        // Multiple pages
        let remainingHeight = imgHeight;
        let currentPosition = 0;
        let pageNumber = 1;

        while (remainingHeight > 0) {
          const pageHeight = Math.min(remainingHeight, pdfHeight - 20);
          
          if (pageNumber > 1) {
            pdf.addPage();
          }

          pdf.addImage(
            imgData, 
            'PNG', 
            10, 
            10, 
            imgWidth, 
            imgHeight,
            undefined,
            'FAST',
            0,
            -currentPosition
          );

          remainingHeight -= (pdfHeight - 20);
          currentPosition += (pdfHeight - 20);
          pageNumber++;
        }
      }

      // Generate filename
      const fileName = `DDR_${well?.WellName || 'Unknown'}_${formatDate(currentReport?.ReportDate)}.pdf`;
      
      // Download PDF
      pdf.save(fileName);
      
      console.log('✅ DDR PDF Export completed successfully');
      return true;

    } catch (error) {
      console.error('❌ DDR PDF Export failed:', error);
      throw error;
    }
  }, []);

  // Main export function
  const exportDDRToPDF = useCallback(async ({
    well,
    currentReport,
    sectionData,
    layoutConfig,
    globalSettings,
    processedHeaderHTML,
    onSuccess,
    onError
  }) => {
    if (isExporting) {
      console.warn('Export already in progress');
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      console.log('🚀 Starting DDR PDF Export Hook');
      console.log('Export parameters:', {
        wellName: well?.WellName,
        reportDate: currentReport?.ReportDate,
        sectionsCount: layoutConfig?.sections?.length,
        hasHeader: !!processedHeaderHTML
      });

      // Validate required parameters
      if (!well) {
        throw new Error('Well information is required for PDF export');
      }

      if (!currentReport) {
        throw new Error('Current report is required for PDF export');
      }

      if (!layoutConfig || !layoutConfig.sections) {
        throw new Error('Layout configuration is required for PDF export');
      }

      // Wait a brief moment to ensure the component is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      // Export to PDF using the rendered content
      await exportToPDF(well, currentReport);
      
      console.log('✅ DDR PDF Export completed successfully');
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      return true;

    } catch (error) {
      console.error('❌ DDR PDF Export failed:', error);
      setExportError(error.message);
      
      // Call error callback if provided
      if (onError) {
        onError(error);
      }
      
      throw error;

    } finally {
      setIsExporting(false);
    }
  }, [isExporting, exportToPDF]);

  // Quick export function (uses current DDR state)
  const quickExportToPDF = useCallback(async (ddrState) => {
    const {
      wells,
      selectedWellId,
      currentReport,
      sectionData,
      layoutConfig,
      globalSettings,
      processedHeaderHTML
    } = ddrState;

    // Find the selected well
    const selectedWell = wells.find(w => w.WellID?.toString() === selectedWellId?.toString());

    if (!selectedWell) {
      throw new Error('No well selected for export');
    }

    return exportDDRToPDF({
      well: selectedWell,
      currentReport,
      sectionData,
      layoutConfig,
      globalSettings,
      processedHeaderHTML
    });
  }, [exportDDRToPDF]);

  // Helper function to validate export readiness
  const validateExportReadiness = useCallback((ddrState) => {
    const {
      wells,
      selectedWellId,
      currentReport,
      layoutConfig
    } = ddrState;

    const issues = [];

    if (!wells || wells.length === 0) {
      issues.push('No wells available');
    }

    if (!selectedWellId) {
      issues.push('No well selected');
    }

    const selectedWell = wells?.find(w => w.WellID?.toString() === selectedWellId?.toString());
    if (!selectedWell) {
      issues.push('Selected well not found');
    }

    if (!currentReport) {
      issues.push('No report selected');
    }

    if (!layoutConfig || !layoutConfig.sections) {
      issues.push('Layout configuration not loaded');
    }

    return {
      isReady: issues.length === 0,
      issues
    };
  }, []);

  // Reset error state
  const clearExportError = useCallback(() => {
    setExportError(null);
  }, []);

  // Create the PDF content renderer function
  const createPDFContentRenderer = useCallback((props) => {
    return (
      <DDRPDFExporter
        ref={exportRef}
        {...props}
      />
    );
  }, []);

  return {
    exportDDRToPDF,
    quickExportToPDF,
    validateExportReadiness,
    createPDFContentRenderer,
    isExporting,
    exportError,
    clearExportError
  };
};

export default useDDRPDFExport; 